'use client';

import { X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { useDebounceCallback } from 'usehooks-ts';

import { TABLET_BREAKPOINT } from '@shared/constants/global';
import { ID_SUBCATEGORY_ATTRIBUTE, ID_TYPE_PRODUCT_ATTRIBUTE, ID_WIDTH_ATTRIBUTE } from '@shared/constants/keys';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import { OptionType } from '@shared/types/global';
import { Modal } from '@shared/ui/Modal';
import { getScreenWidth } from '@shared/ui/ScreenSizeTracking';
import { VStack } from '@shared/ui/Stack';

import {
  changeList,
  changeRange,
  changeSwitch,
  closeAllFiltersModal,
  returnPrevFilterState,
  submitFilters
} from '../../../model/slice/categorySlice';
import { TransformedCategoryFilterItemType } from '../../../model/types/categoryFilters';
import { getFilterLabel } from '../../../utils/getFilterLabel/getFilterLabel';
import { splitArray } from '../../../utils/splitArray/splitArray';
import { Buttons } from './Buttons/Buttons';
import { CheckboxList } from './CheckboxList/CheckboxList';
import { FilterSwitch } from './FilterSwitch/FilterSwitch';
import { InputRange } from './InputRange/InputRange';
import cls from './FilterModal.module.scss';

export const FilterModal = () => {
  const dispatch = useAppDispatch();
  const openModal = useAppSelector((state) => state.category.openAllFilters);
  const filters = useAppSelector((state) => state.category.filters);
  const activeFilters = useAppSelector((state) => state.category.activeFilters);
  const columnFilters = useMemo(
    () =>
      splitArray(
        Object.keys(filters).filter((el) => el !== ID_TYPE_PRODUCT_ATTRIBUTE && el !== ID_SUBCATEGORY_ATTRIBUTE)
      ),
    [filters]
  );
  const [abortController, setAbortController] = useState(new AbortController());
  const width = useAppSelector(getScreenWidth);

  const closeModalHandler = () => {
    dispatch(closeAllFiltersModal());
    dispatch(returnPrevFilterState());
  };

  const submitFilter = useCallback(() => {
    abortController.abort();
    const newAbortController = new AbortController();
    setAbortController(newAbortController);
    dispatch(submitFilters({ update: false, signal: newAbortController.signal }));
  }, [abortController, dispatch]);

  const submitFilterDebounce = useDebounceCallback(submitFilter, 500);

  const changeSwitchHandler = (value: boolean, id: string) => {
    dispatch(changeSwitch({ value, id }));
    submitFilter();
  };

  const changeRangeHandler = (min: string, max: string, id: string) => {
    dispatch(changeRange({ min, max, id }));
    submitFilterDebounce();
  };

  const changeCheckboxListHandler = (options: OptionType[], id: string) => {
    dispatch(changeList({ arr: options, id }));
    submitFilterDebounce();
  };

  const renderFilters = (filter: TransformedCategoryFilterItemType, id: string) => {
    switch (filter.type) {
      case 'Boolean':
        return (
          <FilterSwitch
            filterId={id}
            onChange={changeSwitchHandler}
            value={!!(activeFilters[id] && activeFilters[id][0])}
            key={filter.name}
            label={getFilterLabel(id, filter.name)}
          />
        );
      case 'String':
        return (
          <CheckboxList
            activeFilters={activeFilters}
            filterId={id}
            key={filter.name}
            options={filter.values || []}
            label={getFilterLabel(id, filter.name)}
            onChange={changeCheckboxListHandler}
          />
        );
      case 'Number':
        return (
          <InputRange
            listType={id === ID_WIDTH_ATTRIBUTE ? 'radio-list' : 'range'}
            activeFilters={activeFilters as Record<string, number[]>}
            unit={filter.unit}
            filterId={id}
            baseMaxValue={filter.max ? filter.max.toString() : ''}
            baseMinValue={filter.min ? filter.min.toString() : ''}
            onChange={changeRangeHandler}
            key={filter.name}
            label={getFilterLabel(id, filter.name)}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={openModal}
      fullScreen={width! <= TABLET_BREAKPOINT}
      onClose={closeModalHandler}
    >
      <div
        role={'alert'}
        className={cls.wrap}
        data-testid={'filter-modal'}
      >
        <div className={cls.top}>
          <div className={cls.label}>Фильтры</div>
          <button
            data-testid={'filter-modal-close-btn'}
            onClick={closeModalHandler}
            className={cls.close}
          >
            <X />
          </button>
        </div>
        <div className={cls.inner}>
          <div className={cls.scroll}>
            {columnFilters.map((column, index) => (
              <VStack
                className={cls.col}
                gap={'12'}
                key={index}
              >
                {column.map((filterId) => {
                  return renderFilters(filters[filterId], filterId);
                })}
              </VStack>
            ))}
          </div>
        </div>
        <Buttons />
      </div>
    </Modal>
  );
};
